import React from 'react';
import {
    Grid,
    Box,
    IconButton,
    Typography,
    styled,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Slide } from 'react-slideshow-image';
import 'react-slideshow-image/dist/styles.css';
import Lightbox from 'yet-another-react-lightbox';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import Video from 'yet-another-react-lightbox/plugins/video';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import MapIcon from '@mui/icons-material/Map';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SmartDisplayIcon from '@mui/icons-material/SmartDisplay';
import { NavigateBefore, NavigateNext } from '@mui/icons-material';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';

const CustomIconButton = styled(IconButton)(({ theme }) => ({
    backgroundColor: theme.palette.neutral.main,
    opacity: 0.9,
    padding: theme.spacing(0.5),
    margin: theme.spacing(1),
    ':active': { opacity: 1 },
    transition: '0.2s ease-in-out',
}));

export default function IndividualExperiencePhotos(experience: any) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [open, setOpen] = React.useState(false);
    const [currentIndex, setCurrentIndex] = React.useState(0);
    const [lightboxCollection, setLightboxCollection] = React.useState<
        'all' | 'videos'
    >('all');
    const [mobileIndex, setMobileIndex] = React.useState(0);
    const slideRef = React.useRef<any>(null);

    const photoArray = Array.isArray(experience?.experience?.photos)
        ? experience.experience.photos.map((p: any) => ({
              ...p,
              mediaType: 'photo',
          }))
        : [];

    const videoArray = Array.isArray(experience?.experience?.videos)
        ? experience.experience.videos.map((v: any) => ({
              ...v,
              mediaType: 'video',
          }))
        : [];

    const floorPlanArray = Array.isArray(experience?.experience?.floorPlans)
        ? experience.experience.floorPlans.map((f: any) => ({
              ...f,
              mediaType: 'floorPlan',
          }))
        : [];

    const breakpoints = [1080, 640, 384, 256, 128, 96, 64, 48];

    const photos = photoArray
        .filter((p: any) => !!p?.src)
        .map((p: any) => ({
            src: p.src,
            width: 1080,
            height: 800,
            srcSet: breakpoints.map((bp) => ({
                src: p.src,
                width: bp,
                height: Math.round((800 / 1080) * bp),
            })),
        }));

    const videos = videoArray.map((v: any) => ({
        type: 'video' as const,
        width: 1280,
        height: 720,
        poster: v?.thumbnail,
        autoPlay: false,
        sources: [{ src: v.src, type: 'video/mp4' }],
    }));

    const floorPlans = floorPlanArray
        .filter((f: any) => !!f?.src)
        .map((f: any) => ({
            src: f.src,
            width: 1080,
            height: 800,
            srcSet: breakpoints.map((bp) => ({
                src: f.src,
                width: bp,
                height: Math.round((800 / 1080) * bp),
            })),
        }));

    const firstVideoIndex = videos.length > 0 ? photos.length : -1;
    const firstFloorPlanIndex =
        floorPlans.length > 0 ? photos.length + videos.length : -1;

    const combinedMediaForDesktop = [...photos, ...videos, ...floorPlans];
    const combinedMediaForMobile = [
        ...photoArray,
        ...videoArray,
        ...floorPlanArray,
    ];

    const lightBoxButtons: React.ReactNode[] = [];
    if (
        lightboxCollection === 'all' &&
        (firstFloorPlanIndex >= 0 || firstVideoIndex >= 0)
    ) {
        lightBoxButtons.push(
            <button
                key='photos-button'
                type='button'
                className='yarl__button'
                onClick={() => setCurrentIndex(0)}
                style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.55)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PhotoCameraIcon
                        width={20}
                        height={20}
                        htmlColor={theme.palette.primary.main}
                    />
                    Photos
                </Box>
            </button>,
        );
    }
    if (lightboxCollection === 'all' && firstVideoIndex >= 0) {
        lightBoxButtons.push(
            <button
                key='videos-button'
                type='button'
                className='yarl__button'
                onClick={() => setCurrentIndex(firstVideoIndex)}
                style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.55)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SmartDisplayIcon
                        width={20}
                        height={20}
                        htmlColor={theme.palette.primary.main}
                    />{' '}
                    Videos
                </Box>
            </button>,
        );
    }
    if (lightboxCollection === 'all' && firstFloorPlanIndex >= 0) {
        lightBoxButtons.push(
            <button
                key='floorplans-button'
                type='button'
                className='yarl__button'
                onClick={() => setCurrentIndex(firstFloorPlanIndex)}
                style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.55)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                    <MapIcon
                        width={16}
                        height={16}
                        htmlColor={theme.palette.primary.main}
                    />
                    Floor Plan
                </Box>
            </button>,
        );
    }
    const activeSlides =
        lightboxCollection === 'videos' ? videos : combinedMediaForDesktop;

    const main = photos[0];
    const thumbs = photos.slice(1, 5);
    const extraCount = Math.max(0, photos.length - 5);

    const GAP = 8;
    const RADIUS = 12;
    const BTN_H = 30;

    return (
        <Grid sx={{ width: '100%', height: '100%' }}>
            {photos.length === 0 &&
            videos.length === 0 &&
            floorPlans.length === 0 ? null : (
                <>
                    {isMobile ? (
                        <Grid
                            sx={{
                                aspectRatio: '1/1',
                                position: 'relative',
                                width: '100vw',
                            }}
                        >
                            <Slide
                                ref={slideRef}
                                autoplay={false}
                                transitionDuration={300}
                                easing='ease'
                                onChange={(_, next) => setMobileIndex(next)}
                                nextArrow={
                                    <CustomIconButton>
                                        <NavigateNext fontSize='small' />
                                    </CustomIconButton>
                                }
                                prevArrow={
                                    <CustomIconButton>
                                        <NavigateBefore fontSize='small' />
                                    </CustomIconButton>
                                }
                            >
                                {combinedMediaForMobile.map((m: any) =>
                                    ['photo', 'floorPlan'].includes(
                                        m.mediaType,
                                    ) ? (
                                        <img
                                            key={m.id}
                                            alt={m.id}
                                            src={m.src}
                                            style={{
                                                aspectRatio: '1/1',
                                                width: '100vw',
                                                objectFit: 'cover',
                                            }}
                                        />
                                    ) : (
                                        <video
                                            key={m.id}
                                            src={m.src}
                                            controls
                                            playsInline
                                            style={{
                                                aspectRatio: '1/1',
                                                width: '100vw',
                                                objectFit: 'cover',
                                            }}
                                        />
                                    ),
                                )}
                            </Slide>

                            <Box
                                sx={{
                                    position: 'absolute',
                                    right: 8,
                                    bottom: 36,
                                    zIndex: 2,
                                    px: 1,
                                    py: 0.25,
                                    bgcolor: 'rgba(255,255,255,0.8)',
                                    color: 'primary.main',
                                    borderRadius: '6px',
                                    fontSize: 12,
                                    lineHeight: 1,
                                    pointerEvents: 'none',
                                }}
                            >
                                {mobileIndex + 1}/
                                {combinedMediaForMobile.length}
                            </Box>

                            {(firstFloorPlanIndex >= 0 ||
                                firstVideoIndex >= 0) && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 36,
                                        left: 10,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                        zIndex: 2,
                                    }}
                                >
                                    <CustomButton
                                        variant='contained'
                                        color='neutral'
                                        onClick={() => slideRef.current.goTo(0)}
                                        sx={{
                                            minWidth: 40,
                                            height: 30,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: 'rgba(255,255,255,0.8)',
                                            p: 0,
                                        }}
                                    >
                                        <PhotoCameraIcon
                                            sx={{
                                                fontSize: 20,
                                            }}
                                            htmlColor={
                                                theme.palette.primary.main
                                            }
                                        />
                                    </CustomButton>

                                    {firstVideoIndex >= 0 && (
                                        <CustomButton
                                            variant='contained'
                                            color='neutral'
                                            onClick={() =>
                                                slideRef.current.goTo(
                                                    firstVideoIndex,
                                                )
                                            }
                                            sx={{
                                                minWidth: 40,
                                                height: 30,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor:
                                                    'rgba(255,255,255,0.8)',
                                                p: 0,
                                            }}
                                        >
                                            <SmartDisplayIcon
                                                sx={{
                                                    fontSize: 20,
                                                }}
                                                htmlColor={
                                                    theme.palette.primary.main
                                                }
                                            />
                                        </CustomButton>
                                    )}

                                    {firstFloorPlanIndex >= 0 && (
                                        <CustomButton
                                            variant='contained'
                                            color='neutral'
                                            onClick={() =>
                                                slideRef.current.goTo(
                                                    firstFloorPlanIndex,
                                                )
                                            }
                                            sx={{
                                                minWidth: 40,
                                                height: 30,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor:
                                                    'rgba(255,255,255,0.8)',
                                                p: 0,
                                            }}
                                        >
                                            <MapIcon
                                                sx={{
                                                    fontSize: 18,
                                                }}
                                                htmlColor={
                                                    theme.palette.primary.main
                                                }
                                            />
                                        </CustomButton>
                                    )}
                                </Box>
                            )}
                        </Grid>
                    ) : (
                        <Grid
                            container
                            sx={{
                                height: 350,
                                position: 'relative',
                                gap: `${GAP}px`,
                                flexWrap: 'nowrap',
                            }}
                        >
                            <Grid
                                item
                                xs={12}
                                md={6}
                                sx={{
                                    position: 'relative',
                                    flex: 1,
                                    height: '100%',
                                }}
                            >
                                {main && (
                                    <Box
                                        onClick={() => {
                                            setCurrentIndex(0);
                                            setOpen(true);
                                        }}
                                        component='img'
                                        src={main.src}
                                        alt=''
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            borderRadius: `${RADIUS}px 0 0 ${RADIUS}px`,
                                            cursor: 'pointer',
                                            display: 'block',
                                        }}
                                    />
                                )}

                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 10,
                                        left: 10,
                                        zIndex: 3,
                                        display: 'flex',
                                        gap: 1,
                                    }}
                                >
                                    {firstVideoIndex >= 0 && (
                                        <CustomButton
                                            variant='contained'
                                            color='neutral'
                                            onClick={() => {
                                                setLightboxCollection('videos');
                                                setCurrentIndex(0);
                                                setOpen(true);
                                            }}
                                            sx={{
                                                px: 1.5,
                                                minWidth: 'unset',
                                                height: BTN_H,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor:
                                                    'rgba(255,255,255,0.8)',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: 0.8,
                                                }}
                                            >
                                                <SmartDisplayIcon
                                                    width={18}
                                                    height={18}
                                                    htmlColor={
                                                        theme.palette.primary
                                                            .main
                                                    }
                                                />
                                                <Typography
                                                    variant='caption'
                                                    sx={{ fontSize: '10px' }}
                                                >
                                                    Video
                                                </Typography>
                                            </Box>
                                        </CustomButton>
                                    )}

                                    {firstFloorPlanIndex >= 0 && (
                                        <CustomButton
                                            variant='contained'
                                            color='neutral'
                                            onClick={() => {
                                                setCurrentIndex(
                                                    firstFloorPlanIndex,
                                                );
                                                setOpen(true);
                                            }}
                                            sx={{
                                                px: 1.5,
                                                minWidth: 'unset',
                                                height: BTN_H,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor:
                                                    'rgba(255,255,255,0.8)',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: 0.8,
                                                }}
                                            >
                                                <MapIcon
                                                    width={14}
                                                    height={14}
                                                    htmlColor={
                                                        theme.palette.primary
                                                            .main
                                                    }
                                                />
                                                <Typography
                                                    variant='caption'
                                                    sx={{ fontSize: '10px' }}
                                                >
                                                    Floor Plan
                                                </Typography>
                                            </Box>
                                        </CustomButton>
                                    )}
                                </Box>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={6}
                                sx={{
                                    flex: 1,
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gridTemplateRows: '1fr 1fr',
                                    gap: `${GAP}px`,
                                    height: '100%',
                                }}
                            >
                                {thumbs.map((t: any, i: number) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            position: 'relative',
                                            overflow: 'hidden',
                                            borderRadius:
                                                i === 1
                                                    ? `0 ${RADIUS}px 0 0`
                                                    : i === 3
                                                      ? `0 0 ${RADIUS}px 0`
                                                      : 0,
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => {
                                            setCurrentIndex(1 + i);
                                            setOpen(true);
                                        }}
                                    >
                                        <Box
                                            component='img'
                                            src={t.src}
                                            alt=''
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                display: 'block',
                                            }}
                                        />
                                        {extraCount > 0 &&
                                            i === thumbs.length - 1 && (
                                                <Box
                                                    onClick={() => {
                                                        setCurrentIndex(5);
                                                        setOpen(true);
                                                    }}
                                                    sx={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        bgcolor:
                                                            'rgba(0,0,0,0.45)',
                                                        color: theme.palette
                                                            .neutral.main,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent:
                                                            'center',
                                                        fontWeight: 700,
                                                        fontSize: 22,
                                                        borderRadius: `0 0 ${RADIUS}px 0`,
                                                    }}
                                                >
                                                    +{extraCount} more
                                                </Box>
                                            )}
                                    </Box>
                                ))}
                            </Grid>
                        </Grid>
                    )}

                    <Lightbox
                        index={currentIndex}
                        open={open}
                        close={() => {
                            setOpen(false);
                            setLightboxCollection('all');
                        }}
                        slides={activeSlides}
                        plugins={[Fullscreen, Thumbnails, Zoom, Video]}
                        toolbar={{ buttons: [...lightBoxButtons, 'close'] }}
                        video={{
                            autoPlay: false,
                            controls: true,
                            playsInline: true,
                            loop: true,
                            muted: false,
                            disablePictureInPicture: true,
                            disableRemotePlayback: true,
                            crossOrigin: '',
                            preload: '',
                        }}
                        zoom={{
                            maxZoomPixelRatio: 50,
                            zoomInMultiplier: 2,
                            doubleTapDelay: 0,
                            doubleClickDelay: 0,
                            keyboardMoveDistance: 0,
                            wheelZoomDistanceFactor: 0,
                            pinchZoomDistanceFactor: 0,
                            scrollToZoom: false,
                        }}
                        carousel={{ preload: 1, padding: 0, imageFit: 'cover' }}
                    />
                </>
            )}
        </Grid>
    );
}
